import { useEffect, useState } from "react";

const navLinks = [
  { href: "#feed", label: "今日爆梗" },
  { href: "#featured", label: "深度专题" },
  { href: "#quotes", label: "弹幕金句" },
  { href: "#memes", label: "表情包" },
  { href: "#categories", label: "吃瓜分类" },
  { href: "#faq", label: "常见问题" },
];

interface HeaderProps {
  onSearchOpen: () => void;
}

export default function Header({ onSearchOpen }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const goSection = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/90 backdrop-blur-md shadow-[0_1px_0_oklch(var(--background-200))]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-20 max-w-[1240px] items-center justify-between px-4 md:px-6">
        {/* logo */}
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-2 whitespace-nowrap"
          aria-label="55吃瓜 回到顶部"
        >
          <span className="grid h-9 w-9 md:h-10 md:w-10 place-items-center rounded-2xl bg-primary-500 text-background-50">
            <i className="ri-emotion-laugh-fill text-lg md:text-xl"></i>
          </span>
          <span className="font-heading text-xl md:text-2xl text-foreground-950 leading-none">
            55<span className="text-primary-600">吃瓜</span>
          </span>
        </a>

        {/* desktop nav */}
        <nav aria-label="主导航" className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => {
                e.preventDefault();
                goSection(l.href);
              }}
              className="rounded-full px-3.5 py-2 text-sm font-medium text-foreground-700 whitespace-nowrap transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <button
            onClick={onSearchOpen}
            aria-label="搜索全站爆梗"
            className="flex items-center gap-2 rounded-full bg-background-100 px-4 py-2 text-sm text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-800"
          >
            <i className="ri-search-line"></i>
            <span className="whitespace-nowrap">搜索爆梗</span>
            <kbd className="rounded-md bg-background-50 px-1.5 py-0.5 text-xs text-foreground-400">⌘K</kbd>
          </button>
          <a
            href="#contribute"
            onClick={(e) => {
              e.preventDefault();
              goSection("#contribute");
            }}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-edit-line"></i>
            投稿吐槽
          </a>
        </div>

        {/* mobile toggle */}
        <button
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          className="grid h-10 w-10 place-items-center rounded-xl bg-background-100 text-foreground-800 lg:hidden"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-4-line"} text-xl`}></i>
        </button>
      </div>

      {/* mobile drawer */}
      {menuOpen && (
        <div className="fixed inset-0 top-16 z-40 lg:hidden animate-fade">
          <div className="absolute inset-0 bg-foreground-950/30" onClick={() => setMenuOpen(false)}></div>
          <div className="absolute inset-x-0 top-0 max-h-[calc(100vh-4rem)] overflow-auto bg-background-50 p-5 shadow-lg">
            <nav aria-label="移动端导航" className="flex flex-col gap-1">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => {
                    e.preventDefault();
                    goSection(l.href);
                  }}
                  className="flex items-center justify-between rounded-xl bg-background-100 px-4 py-3 text-base font-medium text-foreground-800"
                >
                  {l.label}
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </a>
              ))}
            </nav>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onSearchOpen();
                }}
                className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-background-100 px-4 py-3 text-sm font-medium text-foreground-700"
              >
                <i className="ri-search-line"></i> 搜索
              </button>
              <a
                href="#contribute"
                onClick={(e) => {
                  e.preventDefault();
                  goSection("#contribute");
                }}
                className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50"
              >
                <i className="ri-edit-line"></i> 投稿吐槽
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}